import { skipToken, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { trpc } from "../../app/trpc.js";
import {
  ClientsIcon,
  type Column,
  DataTable,
  EmptyState,
  emptyLast,
  focusRing,
  InputGroup,
  Notice,
  PageTitle,
  SearchIcon,
  SkeletonList,
  textComparator,
  useWide,
} from "../../ui/index.js";
import { useCabinet } from "../cabinet/index.js";
import { ClientPanel } from "../conversations/index.js";

type Found = { id: string; name: string | null; phone: string | null; noShowCount: number; identities: Array<{ username?: string | null }> };
const handleOf = (c: Found) => c.identities.find((i) => i.username)?.username ?? null;
/** Имя как на карточке: имя, иначе @ник; без того и другого пусто (в сортировке уходит в конец). */
const displayName = (c: Found) => c.name || (handleOf(c) ? `@${handleOf(c)}` : "");

/**
 * Клиенты списком и поиск по имени, телефону или @нику. Владелец видит всех клиентов салона, мастер только своих
 * (у кого есть записи к нему; решение 04.11.2026), это решает сервер. На телефоне карточки-ссылки; на широком экране таблица
 * с сортировкой, выбранный клиент справа в панели (контакты, заметки, визиты, ссылка на карточку).
 * Запрос и выбранный клиент живут в адресе (`?q=…&client=…`, replace): после карточки и «Назад» всё на месте.
 */
export function ClientsPage() {
  const { t, i18n } = useTranslation();
  const cab = useCabinet();
  const wide = useWide();
  const [params, setParams] = useSearchParams();
  const debounced = params.get("q") ?? "";
  const selected = params.get("client");
  const [query, setQuery] = useState(debounced);
  useEffect(() => {
    const id = setTimeout(() => {
      const q = query.trim();
      if (q === debounced) return;
      setParams(
        (p) => {
          if (q) p.set("q", q);
          else p.delete("q");
          // Выбранный клиент остаётся открытым и при новом поиске: список виден сразу, клиента могли выбрать,
          // пока запрос ещё набирается.
          return p;
        },
        { replace: true },
      );
    }, 300);
    return () => clearTimeout(id);
  }, [query, debounced, setParams]);
  const setSelected = (id: string) =>
    setParams(
      (p) => {
        p.set("client", id);
        return p;
      },
      { replace: true },
    );
  const text = textComparator(i18n.language);
  const found = useQuery(trpc.calendar.clients.find.queryOptions(cab.loading ? skipToken : { salonId: cab.salonId, query: debounced }));
  const columns: Column<Found>[] = [
    {
      key: "name",
      title: t("clients.colName"),
      sortWithDir: (dir) => emptyLast(displayName, text, dir),
      render: (c) => <span className="font-medium">{displayName(c) || t("clients.noName")}</span>,
    },
    { key: "phone", title: t("clients.colPhone"), sortWithDir: (dir) => emptyLast((c: Found) => c.phone, text, dir), render: (c) => c.phone ?? "" },
    {
      key: "instagram",
      title: t("clients.colInstagram"),
      sortWithDir: (dir) => emptyLast(handleOf, text, dir),
      render: (c) => (handleOf(c) ? `@${handleOf(c)}` : ""),
    },
    {
      key: "noShows",
      title: t("clients.colNoShows"),
      sort: (a, b) => a.noShowCount - b.noShowCount,
      className: "text-right",
      render: (c) => (c.noShowCount > 0 ? <span className="text-red-700">{c.noShowCount}</span> : <span className="text-ink-3">0</span>),
    },
  ];

  return (
    <div className={wide ? "grid grid-cols-[minmax(0,1fr)_18rem] gap-6" : ""}>
      <div>
        <PageTitle>{t("clients.title")}</PageTitle>
        <InputGroup
          type="search"
          before={<SearchIcon className="h-5 w-5" />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("clients.searchPlaceholder")}
          autoComplete="off"
        />
        <div className="mt-4 space-y-3">
          {!debounced && <p className="text-sm text-ink-3">{cab.isOwner ? t("clients.allHint") : t("clients.mineHint")}</p>}
          {found.isPending && <SkeletonList rows={3} />}
          {found.isError && <Notice kind="error">{t("common.error")}</Notice>}
          {found.data?.length === 0 && (
            <EmptyState icon={<ClientsIcon />} title={debounced ? t("clients.notFound") : cab.isOwner ? t("clients.emptyAll") : t("clients.emptyMine")} />
          )}
          {wide && found.data && found.data.length > 0 && (
            <DataTable
              label={t("clients.title")}
              columns={columns}
              rows={found.data}
              rowKey={(c) => c.id}
              selectedKey={selected}
              onRowClick={(c) => setSelected(c.id)}
              rowLabel={(c) => displayName(c) || c.phone || t("clients.noName")}
              defaultSort={{ key: "name", dir: "asc" }}
            />
          )}
          {!wide &&
            found.data?.map((c) => {
              const handle = c.identities.find((i) => i.username)?.username;
              return (
                <Link
                  key={c.id}
                  to={`${cab.base}/clients/${c.id}`}
                  className={`block rounded-card border border-line bg-white px-4 py-3 shadow-card transition hover:border-line-strong ${focusRing}`}
                >
                  <div className="font-medium text-ink">{c.name || (handle ? `@${handle}` : t("clients.noName"))}</div>
                  <div className="text-sm text-ink-3">
                    {[c.phone, handle && `@${handle}`].filter(Boolean).join(" · ")}
                    {c.noShowCount > 0 && <span className="text-red-700"> · {t("clients.noShows", { count: c.noShowCount })}</span>}
                  </div>
                </Link>
              );
            })}
        </div>
      </div>
      {wide && (
        <div className="sticky top-4 max-h-[calc(100dvh-2rem)] self-start overflow-y-auto pt-12">
          {selected ? (
            <ClientPanel key={selected} clientId={selected} />
          ) : (
            <div className="rounded-card border border-dashed border-line-strong">
              <EmptyState icon={<ClientsIcon />} title={t("clients.selectHint")} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
